import { isImage, parseImage } from './helpers/imageHelpers';
import { isHeader, parseHeader } from './helpers/headerHelpers';
import { isHorizontalRule, createHorizontalRuleObject } from './helpers/horizontalRuleHelpers';
import { isListItem, isNumberedItem, parseListItem } from './helpers/listItemHelpers';
import { parseInlineFormatting, removeBreakTags } from './helpers/inlineFormattingHelpers';

const NEWLINE_CHARACTER = '\n';
const SPACE_CHARACTER = ' ';

export async function processMarkdownLine(line: string): Promise<any[]> {
    const elements: any[] = [];

    switch (true) {
        case isImage(line): {
            await handleImageLine(line, elements);
            break;
        }
        case isHeader(line): {
            handleHeaderLine(line, elements);
            break;
        }
        case isHorizontalRule(line): {
            handleHorizontalRuleLine(elements);
            break;
        }
        case isListItem(line):
        case isNumberedItem(line): {
            handleListItemLine(line, elements);
            break;
        }
        default: {
            handleTextLine(line, elements);
            break;
        }
    }

    elements.forEach(el => {
        if (el.type === 'text' && typeof el.content.text === 'string') {
            el.content.text = unescapeMarkdown(el.content.text);
        }
    });

    ensureNewlineAtEnd(elements);

    return elements;
}

async function handleImageLine(line: string, elements: any[]): Promise<void> {
    const imageElement = await parseImage(line);
    if (imageElement) {
        elements.push({ type: 'image', content: imageElement });
    }
}

function handleHeaderLine(line: string, elements: any[]): void {
    const headerElements = parseHeader(line);
    if (headerElements) {
        elements.push(...headerElements.map(el => ({ type: 'text', content: el })));
    }
}

function handleHorizontalRuleLine(elements: any[]): void {
    elements.push({ type: 'horizontalRule', content: createHorizontalRuleObject() });
}

function handleListItemLine(line: string, elements: any[]): void {
    elements.push(...parseListItem(line).map(el => ({ type: 'text', content: el })));
}

function handleTextLine(line: string, elements: any[]): void {
    const cleanedLine = line.replace(NEWLINE_CHARACTER, SPACE_CHARACTER).trim();
    elements.push(...parseInlineFormatting(removeBreakTags(cleanedLine)).map(el => ({ type: 'text', content: el })));
}

function ensureNewlineAtEnd(elements: any[]): void {
    if (elements.length === 0 || elements[elements.length - 1].content.text !== NEWLINE_CHARACTER) {
        elements.push({ type: 'text', content: { text: NEWLINE_CHARACTER } });
    }
}

function unescapeMarkdown(text: string): string {
    return text
        .replace(/\\\./g, '.')   // 1\. → 1.
        .replace(/\\([-*+])/g, '$1'); // \- → -, \* → *, \+ → +
}
